import { useMemo, type ReactNode } from 'react'

import { ContextoDeFlags, flagsDoCliente, type Flags } from '@/flags/flags'

/**
 * Serves the flags of the client in scope — story 0.4 AC-8.
 *
 * ⚠️ **Derived during render, not synchronised with an effect.** The first version
 * held the flags in state and refreshed them from two `useEffect`s: one on
 * `idCliente`, one subscribed to story 0.5's `aoTrocarDeCliente()`. Both were
 * unnecessary, and the lint rule that flagged it was right —
 * *"you might not need an effect"*.
 *
 * The reasoning, because it is not obvious: the selector **changes the route**, so
 * `idCliente` is already the signal. `aoTrocarDeCliente` fires slightly earlier,
 * before navigation completes, but nothing renders the new tenant's data in that
 * window precisely because the URL has not changed yet — so subscribing bought a
 * cascading re-render and no correctness at all.
 *
 * AC-8 is still satisfied, and more directly: switching client re-resolves the
 * flags because the client is an input to the computation rather than something a
 * listener has to remember to push.
 */
export function FlagsProvider({
  idCliente,
  featureFlags,
  modulosHabilitados,
  children,
}: {
  /**
   * The tenant in scope. Explicitly `| undefined` rather than optional, because
   * `exactOptionalPropertyTypes` distinguishes "absent" from "present but
   * undefined", and the caller reads it from `useParams()` where undefined is the
   * normal answer on a route that is not client-scoped (`/admin/*`, BR-23's cycle).
   */
  idCliente: string | undefined
  /**
   * As `featureFlags` do cliente em escopo, como o `/eu` as entrega. Chegam por
   * PROP, e não por `useSession()`, para este provider continuar montável sem
   * sessão — é assim que a suíte o exercita. Quem as lê do perfil é o `AppShell`.
   */
  featureFlags?: Flags | undefined
  /**
   * `modulosHabilitados` do cliente em escopo, do `/eu` — de onde saem as flags
   * `modulo.*` (14/10/2026). Ausente = perfil não carregado: vale o stub, tudo ligado.
   */
  modulosHabilitados?: readonly string[] | undefined
  children: ReactNode
}) {
  const flags = useMemo(
    () => flagsDoCliente(idCliente, featureFlags, modulosHabilitados),
    [idCliente, featureFlags, modulosHabilitados],
  )

  return <ContextoDeFlags.Provider value={flags}>{children}</ContextoDeFlags.Provider>
}
